<template>
  <div class="body edit-graphic">
    <template v-if="template">
      <div class="graphic-editor">
        <div class="flex h-full flex-col pr-3 graphic-edit-layers">
          <label class="tb-label">
            <span
              v-if="isSavedGraphic"
              title="Saved graphic"
              class="fas fa-bookmark text-primary mr-2"
            />

            Graphic title
          </label>

          <input
            class="tb-input mb-4"
            type="text"
            v-model="title"
            data-entity="producer-editor-editTitle"
            data-test="editor-graphics-title"
            @keyup.right.stop
            @keyup.left.stop
            @keyup="onChange"
          />

          <label class="tb-label">Layers</label>

          <layers
            v-model="template.elements"
            :selected="selectedElement"
            :graphic_fields="item.fields"
            @select="selectElement"
            @change="onChange"
          />
        </div>

        <div class="graphic-edit-fields" data-test="graphics-editor-settings">
          <template v-if="selectedElement">
            <component
              :key="selectedElement.id"
              :template="template"
              :graphic="editing"
              :element="selectedElement"
              :graphic_fields="item.fields"
              :production_context="productionContext"
              :is="getElementType(selectedElement.type, selectedElement)"
              @value="update(selectedElement, $event)"
              @update-element="update"
            />
          </template>

          <p v-else class="empty-text text-center">
            Select a layer to start editing
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="js">
/* eslint-disable no-param-reassign, vue/no-mutating-props */
import { cloneDeep, filter, merge } from 'lodash-es';
import dayjs from 'dayjs';

import LayersComponent from './layers/Layers.vue';
import Reactive from '../../utils/reactive';
import ConduitEventsService from '../../services/conduitEventsService';

import ImageSettingComponent from './settings/image/ImageSettings.vue';
import FoxForesightSettingsComponent from './settings/data/FoxForesightSettings.vue';
import VideoSetting from './settings/video/VideoSettings.vue';
import TextSettingComponent from './settings/text/TextSettings.vue';
import ShapeSettingComponent from './settings/shape/ShapeSettings.vue';
import SlideshowWidgetSettingComponent from './settings/widgets/slideshow/SlideShowSettings.vue';
import DateWidgetSettingComponent from './settings/widgets/date/DateSettings.vue';
import QRCodeWidgetSettingComponent from './settings/widgets/qrcode/QRCodeSettings.vue';
import SocialDataSettings from './settings/data/Social.vue';
import SocialDisplaySettings from './settings/data/SocialDisplaySettings.vue';
import PollDisplaySettings from './settings/data/PollDisplaySettings.vue';
import ModalComponent from '../modal/Modal.vue';
import AlertComponent from '../alert/Alert.vue';
import SheetDataSettings from './settings/data/SheetDataSettings.vue';
import DataListSettings from './settings/data/DataListSettings.vue';
import SportradarSettings from './settings/data/Sportradar.vue';
import TimerWidgetSettingsComponent from './settings/widgets/timer/TimerSettings.vue';
import MarqueeSettings from './settings/marquee/marqueeWrapper.vue';
import ShoppableSettings from './settings/data/ShoppableSettings.vue';
import FrameSettings from './settings/data/FrameSettings.vue';

import setDeep from '../../utils/set-deep';
import GraphicsService from '../../services/graphics';

export default {
  name: 'GraphicEditor',
  props: ['editing', 'item'],

  data() {
    return {
      template: null,
      selectedElement: null,
      ready: false,
      original: cloneDeep(this.item.fields),
      originalTitle: this.item.title,
      newGraphicModalOpen: false,
      newGraphicTitle: '',
      isQrCodeEnabled: process.env.QR_CODE_SWITCH === 'ON',
    };
  },

  components: {
    modal: ModalComponent,
    alert: AlertComponent,
    layers: LayersComponent,
    'marquee-text-settings': TextSettingComponent,
    'marquee-image-settings': ImageSettingComponent,
    'text-settings': TextSettingComponent,
    'image-settings': ImageSettingComponent,
    'foxforesight-settings': FoxForesightSettingsComponent,
    'shape-settings': ShapeSettingComponent,
    'slideshow-settings': SlideshowWidgetSettingComponent,
    'date-settings': DateWidgetSettingComponent,
    'qrcode-settings': QRCodeWidgetSettingComponent,
    'timer-settings': TimerWidgetSettingsComponent,
    'video-settings': VideoSetting,
    SheetDataSettings,
    DataListSettings,
    SocialDataSettings,
    SocialDisplaySettings,
    PollDisplaySettings,
    SportradarSettings,
    MarqueeSettings,
    ShoppableSettings,
    FrameSettings,
    'social-display-settings': SocialDisplaySettings,
    'poll-display-settings': PollDisplaySettings,
  },

  mounted() {
    const graphic = this.$store.state.graphics.lookupTable[this.item._id];
    this.original = cloneDeep(graphic?.fields || this.item.fields);
    this.originalTitle = cloneDeep(graphic?.title || this.item.title);
  },

  provide() {
    return {
      onWidgetAction: (action) => {
        if (!this.item.fields[action.elementId]) {
          // Set visibility, which will create necessary fields object for us
          this.update({ id: action.elementId }, { field: 'visible', value: true });
        }

        // Note: mutating the item by reference which caused me confusion,
        // so maybe this note will help someone else.
        const timerFields = this.item.fields[action.elementId];

        const findTimerWidget = (elements) => {
          for (let i = 0; i < elements.length; i += 1) {
            const element = elements[i];

            if (element.id === action.elementId) {
              return element;
            }

            if (element.elements) {
              const timerWidget = findTimerWidget(element.elements);

              if (timerWidget) {
                return timerWidget;
              }
            }
          }

          return null;
        };

        const timerGraphic = findTimerWidget(this.template.elements);
        if (!timerGraphic) { return; }

        if (action.value === 'start') {
          timerFields.startMoment = dayjs();
          timerFields.playTimer = true;
        }

        if (action.value === 'pause') {
          timerFields.playTimer = false;

          const duration = dayjs().diff(timerFields.startMoment, timerGraphic.timerType === 'Stopwatch' ? '' : 'seconds');
          timerFields.duration = timerFields.duration ? timerFields.duration += duration : duration;

          delete timerFields.startMoment;
        }

        if (action.value === 'reset') {
          timerFields.playTimer = false;

          delete timerFields.duration;
          delete timerFields.startMoment;
        }

        if (this.productionContext.live) {
          this.$store.dispatch('updateTimeline', { force: true });
          this.$store.dispatch('sendAction', action);
        }

        if (this.productionContext.preview) {
          this.$store.dispatch('updatePreviewTimeline', { force: true });
          this.$store.dispatch('sendPreviewAction', action);
        }

        ConduitEventsService.track(
            'producer.toggled.timer-widget',
            {
              action: action.value,
              timerFormat: timerGraphic.timerFormat,
              timerType: timerGraphic.timerType,
            },
            {
              ownerId: this.$store.state.production.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
        );
      },
    };
  },

  created() {
    GraphicsService.getTemplate(this.item.template_id)
      .then(async (template) => {
        this.template = template;

        this.template.elements.forEach((element) => {
          this.$store.dispatch('registerDataModule', { element });
        });
      });
  },

  computed: {
    title: {
      get() {
        return this.item.title;
      },

      set(title) {
        // eslint-disable-next-line vue/no-mutating-props
        this.item.title = title;
      },
    },

    timeline() {
      return this.$store.state.production.production.timeline;
    },

    postLookupTable() {
      return this.$store.state.posts.lookupTable;
    },

    // Parent element if graphic is inside a group
    parent() {
      return this.timeline
        .filter((i) => i.layout === 'graphics')
        .find((i) => (
          i.timeline_id !== this.editing.timeline_id
          && !!i.posts.find((p) => p.timeline_id === this.editing.timeline_id)
        ));
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      const id = this.editing.timeline_id;
      const parentId = this.parent?.timeline_id;
      return [id, parentId].includes(this.currentLayoutIndex);
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      const id = this.editing.timeline_id;
      const parentId = this.parent?.timeline_id;
      return [id, parentId].includes(this.currentSampleLayoutIndex);
    },

    unsaved() {
      return (
        this.item.title !== this.originalTitle
        || JSON.stringify(this.item.fields) !== JSON.stringify(this.original)
      );
    },

    isSavedGraphic() {
      return this.item && this.item._id;
    },

    productionContext() {
      return {
        live: this.isCurrentLayout,
        preview: this.isCurrentSampleLayout,
      };
    },

    hasSportradar() {
      // TODO: does this need to be recursive? i.e. sportradar not a root layer
      return this.template.elements.some((el) => (el.type === 'sportradar'));
    },
  },
  methods: {
    /**
     * onChange is a debounced handler called when:
     * 1) Title changes (debounce for obvi reasons)
     * 2) Layer visibility toggled (debounce because users probably toggle a lot of layers at once)
     */
    onChange() {
      clearTimeout(this.changeTimeout);
      this.changeTimeout = setTimeout(() => {
        // Send updates to connected clients ONLY if graphic is not live
        this.sendUpdate({ preview: true });
        this.sendUpdate();
      }, 1e3);
    },

    async sendUpdate({ preview, force } = {}) {
      const dispatchType = preview ? 'updatePreviewTimeline' : 'updateTimeline';
      this.$store.dispatch(dispatchType, { force });
    },

    getElementType(type, element) {
      // Check if this is a Fox Foresight element
      if (type === 'image' && element?.isFoxForesight) {
        return 'foxforesight-settings';
      }

      if (['oval', 'rectangle', 'triangle'].includes(type)) {
        return 'shape-settings';
      }

      return `${type}-settings`;
    },

    selectElement(element) {
      if (element.editable) {
        this.selectedElement = merge(element, this.item.fields[element.id] || {});
      }
    },

    async update(element, obj) {
      // Find elements in graphic with entries (if we're a slideshow template)
      // OR find elements within a group (within a slideshow template)
      if (element.id && element.template_id) {
        // We actually only need this because of the color picker
        // not properly bubbling up value changes, but just in case, do it always
        const withEntries = filter(this.item.fields, (o) => o.entries && o.entries.length);
        let ref;

        // Now we have to iterate through these entries
        // to find which 'field' we actually changed in the graphic
        if (withEntries.length) {
          withEntries.forEach((entry) => {
            entry.entries.forEach((e) => {
              if (e.fields && e.fields.length) {
                e.fields.forEach((field) => {
                  // Are we a top level slideshow element
                  if (field.id === element.id) {
                    ref = field;
                  }

                  // Or are we an element in a group (in a slideshow #inception)
                  if (field.entries) {
                    field.entries.forEach((entry) => { // eslint-disable-line no-shadow
                      if (entry.id === element.id) {
                        ref = entry;
                      }
                    });
                  }
                });
              }
            });
          });

          if (ref) {
            setDeep(ref, obj.field.split('.'), obj.value);
          }
        }
      }

      if (!this.item.fields[element.id]) {
        // No fields yet, so we need to initialize object
        const fields = {
          visible: true,
        };

        if (obj.field.includes('.')) {
          setDeep(fields, obj.field.split('.'), obj.value);
        } else {
          fields[obj.field] = obj.value;
        }

        // eslint-disable-next-line vue/no-mutating-props
        Reactive.createReactiveNestedObject(this.item.fields, {
          [element.id]: fields,
        });
      } else if (element.type === 'video' || obj.field.includes('.')) {
        setDeep(this.item.fields[element.id], obj.field.split('.'), obj.value);
      } else {
        this.item.fields[element.id][obj.field] = obj.value;
      }

      this.onChange();
    },
  },
};
</script>
